import type { BarPoint } from '../../api/marketData'
import { nyCalendarDate } from './session'

/**
 * Simple Moving Average — per-bar output.
 *
 * Returns an array the same length as `values`. The first `length - 1`
 * entries are `null` (warm-up). Thereafter each entry is the SMA of the
 * preceding `length` values.
 */
export function sma(values: number[], length: number): (number | null)[] {
  const out: (number | null)[] = new Array(values.length)
  if (length <= 0 || values.length < length) {
    // Not enough bars for full window — use partial average so the line
    // renders immediately rather than disappearing entirely.
    if (values.length === 0) return out
    let sum = 0
    for (let i = 0; i < values.length; i++) sum += values[i]
    const partialAvg = sum / values.length
    for (let i = 0; i < values.length; i++) out[i] = partialAvg
    return out
  }
  // Compute initial window sum
  let sum = 0
  for (let i = 0; i < length; i++) sum += values[i]
  out[length - 1] = sum / length
  // Slide the window
  for (let i = length; i < values.length; i++) {
    sum += values[i] - values[i - length]
    out[i] = sum / length
  }
  out.fill(null, 0, length - 1)
  return out
}

/**
 * Exponential Moving Average — per-bar output.
 *
 * Seed: SMA of the first `length` values.
 * Subsequent: `alpha * value + (1 - alpha) * prev`
 * where `alpha = 2 / (length + 1)`.
 */
export function ema(values: number[], length: number): (number | null)[] {
  const out: (number | null)[] = new Array(values.length)
  if (length <= 0 || values.length < length) {
    // Not enough bars for warm-up — seed with first valid value so the line
    // renders immediately rather than disappearing entirely.
    if (values.length === 0) return out
    const seed = values[0]
    if (!Number.isFinite(seed)) {
      for (let i = 0; i < values.length; i++) out[i] = null
      return out
    }
    for (let i = 0; i < values.length; i++) out[i] = seed
    return out
  }
  const alpha = 2 / (length + 1)
  // Seed: SMA of first `length` bars
  let seed = 0
  for (let i = 0; i < length; i++) seed += values[i]
  out[length - 1] = seed / length
  // EMA from there
  for (let i = length; i < values.length; i++) {
    out[i] = alpha * values[i] + (1 - alpha) * out[i - 1]!
  }
  out.fill(null, 0, length - 1)
  return out
}

/**
 * Typical price for a single bar: `(high + low + close) / 3`.
 */
export function hlc3(bar: BarPoint): number {
  return (bar.h + bar.l + bar.c) / 3
}

/**
 * Wilder's Average Directional Index (ADX).
 *
 * Returns an array the same length as `bars`. Warm-up entries before the
 * first valid ADX (roughly `2 * length` bars in) are `null`. The series is
 * monotonically non-decreasing in resolution — once it starts emitting
 * numbers, every subsequent bar has a value.
 *
 * Implementation follows Wilder's original smoothing (RMA), not an EMA:
 *   - TR  = max(h-l, |h-prevClose|, |l-prevClose|)
 *   - +DM = up > down && up > 0 ? up : 0    (up   = h - prevHigh)
 *   - -DM = down > up && down > 0 ? down : 0 (down = prevLow - l)
 *   - First smoothed value = sum over the first `length` bars
 *   - Subsequent          = prev - prev / length + current
 *   - +DI = 100 * smPlusDM  / smTR
 *   - -DI = 100 * smMinusDM / smTR
 *   - DX  = 100 * |+DI - -DI| / (+DI + -DI)
 *   - ADX = Wilder-smoothed DX over `length` bars
 */
export function adx(bars: BarPoint[], length = 14): (number | null)[] {
  const n = bars.length
  const out: (number | null)[] = new Array(n).fill(null)
  if (length <= 0 || n < length * 2 + 1) return out

  const tr: number[] = new Array(n)
  const plusDM: number[] = new Array(n)
  const minusDM: number[] = new Array(n)
  tr[0] = 0
  plusDM[0] = 0
  minusDM[0] = 0
  for (let i = 1; i < n; i++) {
    const prev = bars[i - 1]
    const cur = bars[i]
    const range1 = cur.h - cur.l
    const range2 = Math.abs(cur.h - prev.c)
    const range3 = Math.abs(cur.l - prev.c)
    tr[i] = Math.max(range1, range2, range3)
    const up = cur.h - prev.h
    const down = prev.l - cur.l
    plusDM[i] = up > down && up > 0 ? up : 0
    minusDM[i] = down > up && down > 0 ? down : 0
  }

  // Seed: sum of bars 1..length (inclusive).
  let smTR = 0
  let smPlus = 0
  let smMinus = 0
  for (let i = 1; i <= length; i++) {
    smTR += tr[i]
    smPlus += plusDM[i]
    smMinus += minusDM[i]
  }

  const dx: number[] = new Array(n)
  function computeDx(): number {
    if (smTR <= 0) return 0
    const pDI = (100 * smPlus) / smTR
    const mDI = (100 * smMinus) / smTR
    const sum = pDI + mDI
    return sum > 0 ? (100 * Math.abs(pDI - mDI)) / sum : 0
  }
  dx[length] = computeDx()
  for (let i = length + 1; i < n; i++) {
    smTR = smTR - smTR / length + tr[i]
    smPlus = smPlus - smPlus / length + plusDM[i]
    smMinus = smMinus - smMinus / length + minusDM[i]
    dx[i] = computeDx()
  }

  // First ADX = mean of DX[length..2*length-1]; thereafter Wilder-smooth.
  let adxSum = 0
  for (let i = length; i < length * 2; i++) adxSum += dx[i]
  let adxVal = adxSum / length
  out[length * 2 - 1] = adxVal
  for (let i = length * 2; i < n; i++) {
    adxVal = (adxVal * (length - 1) + dx[i]) / length
    out[i] = adxVal
  }
  return out
}

/**
 * Cumulative VWAP that resets at each NY calendar-day boundary (midnight ET).
 *
 * - Premarket, RTH, and after-hours bars on the same calendar day share one
 *   cumulative VWAP. This matches TradingView's default behavior when extended
 *   hours are visible on the chart.
 * - Zero-volume bars do not contribute to the running sums but carry the
 *   last computed VWAP value forward.
 * - If no non-zero-volume bar has been seen yet on the current calendar day,
 *   returns `null` for that bar.
 * - All-zero-volume symbols produce an array of `null` (no crash, no NaN).
 */
export function cumulativeVwap(bars: BarPoint[]): (number | null)[] {
  const out: (number | null)[] = new Array(bars.length)
  let cumPriceVol = 0
  let cumVol = 0
  let lastDate: string | null = null

  for (let i = 0; i < bars.length; i++) {
    const bar = bars[i]
    const date = nyCalendarDate(bar.t)

    // New NY date — reset
    if (date !== lastDate) {
      cumPriceVol = 0
      cumVol = 0
      lastDate = date
    }

    if (bar.v === 0) {
      // Zero volume: carry forward last VWAP (or null if nothing seen yet)
      out[i] = cumVol > 0 ? cumPriceVol / cumVol : null
      continue
    }

    const tp = hlc3(bar)
    cumPriceVol += tp * bar.v
    cumVol += bar.v
    out[i] = cumPriceVol / cumVol
  }

  return out
}
